import { LegalLayout } from "./_legal-layout";

export default function TermsPage() {
  return (
    <LegalLayout
      kicker="// legal · 01"
      title={
        <>
          Terms of <span className="font-italic-serif text-secondary">Service.</span>
        </>
      }
      effectiveDate="January 1, 2026"
      intro={
        <p>
          These Terms of Service (the &ldquo;Terms&rdquo;) govern your access to and use of
          MainLoop (the &ldquo;Service&rdquo;), an online coding school for middle and
          high school students operated by MainLoop Education (&ldquo;MainLoop&rdquo;,
          &ldquo;we&rdquo;, &ldquo;us&rdquo;). By creating an account, enrolling in a
          course, or otherwise using the Service, you agree to these Terms.
        </p>
      }
    >
      <h2>1. Who can use MainLoop</h2>
      <p>
        MainLoop is designed for students ages 11&ndash;18. If you are under 13, a parent
        or legal guardian must create your account and agree to these Terms on your behalf.
        If you are between 13 and 17, you may create an account yourself, but you confirm
        that a parent or guardian has reviewed and approved your enrollment in any paid
        course.
      </p>
      <p>
        By using the Service you represent that the information you provide is accurate
        and that you (or your parent/guardian, where applicable) have the legal capacity
        to enter into these Terms.
      </p>

      <h2>2. Accounts and security</h2>
      <ul>
        <li>You are responsible for keeping your sign-in credentials confidential.</li>
        <li>
          You may not share your account, sell access, or use the Service on behalf of
          another person without their permission.
        </li>
        <li>Notify us immediately if you suspect unauthorized access to your account.</li>
      </ul>

      <h2>3. Courses, enrollment, and live sessions</h2>
      <p>
        Courses are offered as either live cohorts (held over Zoom) or self-paced video
        courses. Live cohort schedules, instructors, and content may change with
        reasonable notice. Recordings of live sessions, when made available, are for the
        personal, non-commercial use of enrolled students only.
      </p>
      <p>
        Enrollment in a paid course grants you a limited, non-transferable, revocable
        license to access the course materials for the duration of the cohort or, for
        self-paced courses, for the access period stated at checkout.
      </p>

      <h3>3.1 Code of conduct</h3>
      <p>You agree to:</p>
      <ul>
        <li>Treat instructors, classmates, and staff with respect.</li>
        <li>Not record, redistribute, or publicly post live class sessions.</li>
        <li>Not harass, discriminate against, or threaten any other user.</li>
        <li>Not submit work that is not your own as if it were.</li>
      </ul>
      <p>We may suspend or terminate accounts that violate these standards.</p>

      <h2>4. Payments, refunds, and cancellations</h2>
      <p>
        Tuition is due at the time of enrollment unless we agree in writing to a payment
        plan. All prices are listed in U.S. dollars. We may offer scholarships or
        discounts at our discretion.
      </p>
      <p>
        <strong>Refund policy:</strong> for live cohorts, you may request a
        full refund up to seven (7) days before the first session, and a 50% refund up to
        the start of the second session. After that point, tuition is non-refundable
        except where required by law. Self-paced courses are non-refundable once accessed.
      </p>

      <h2>5. Your code and content</h2>
      <p>
        You retain ownership of any code, projects, or other content you create using the
        Service (&ldquo;Student Work&rdquo;). By saving Student Work to MainLoop or sharing
        it via a public share link (e.g., <code>/s/:id</code>), you grant MainLoop a
        worldwide, non-exclusive, royalty-free license to host, display, and distribute
        that Student Work as needed to operate and promote the Service.
      </p>
      <p>
        You represent that your Student Work does not infringe anyone&rsquo;s rights and
        does not contain unlawful, harmful, or infringing material.
      </p>

      <h2>6. AI features</h2>
      <p>
        Some courses use generative AI tools (&ldquo;AI Features&rdquo;). AI Features may
        be inaccurate or produce unexpected output. You are responsible for reviewing AI
        output before relying on it. Do not submit confidential or personal information
        you do not want processed by a third-party AI provider into AI Features.
      </p>

      <h2>7. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for anything illegal or to harm others.</li>
        <li>Reverse engineer, scrape, or attempt to disrupt the Service.</li>
        <li>Upload malware or attempt to exploit security vulnerabilities.</li>
        <li>Use the Service to train competing AI models or build a competing offering.</li>
      </ul>

      <h2>8. Third-party services</h2>
      <p>
        The Service integrates with third-party providers (such as Zoom for live classes,
        Stripe for payments, Clerk for authentication, and AI model providers). Your use
        of those services is subject to their own terms and privacy policies. We are not
        responsible for third-party services.
      </p>

      <h2>9. Disclaimers</h2>
      <p>
        The Service is provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo;
        MainLoop makes no warranties, express or implied, including warranties of
        merchantability, fitness for a particular purpose, or non-infringement. We do not
        guarantee any specific learning outcome, employment outcome, or college
        admissions outcome.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, MainLoop&rsquo;s total liability for any
        claim arising out of or relating to the Service is limited to the amount you paid
        MainLoop in the twelve (12) months before the event giving rise to the claim. We
        are not liable for indirect, incidental, special, consequential, or punitive
        damages.
      </p>

      <h2>11. Termination</h2>
      <p>
        You may stop using the Service at any time. We may suspend or terminate your
        access if you violate these Terms or if your account is inactive for an extended
        period. Sections that by their nature should survive termination (such as
        Sections 5, 9, 10) will survive.
      </p>

      <h2>12. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. If we make material changes, we will
        notify enrolled students by email and post a notice on this page at least 14 days
        before the changes take effect.
      </p>

      <h2>13. Governing law &amp; disputes</h2>
      <p>
        These Terms are governed by the laws of the State of New York, without regard
        to conflict-of-laws principles. Any dispute will be resolved in the state or
        federal courts located in New York County, New York, unless we
        agree otherwise in writing.
      </p>

      <h2>14. Contact</h2>
      <p>
        Questions about these Terms? Email{" "}
        <a href="mailto:info@mainloopacademy.com">info@mainloopacademy.com</a>. MainLoop
        Education is based in New York, NY, USA.
      </p>
    </LegalLayout>
  );
}
